import React, { useEffect, useState, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";

import CatalogueCategory from "./CatalogueCategory";
import CatalogueModals from "./CatalogueModals";
import useCatalogue from "../../../services/Order/UseCatalogue";
import useOrderSetting from "../../../services/Order/useOrderSetting";
import useInventory from "../../../services/Inventory/useInventory";
import useGlobalInventory from "../../../services/Inventory/useGlobalInventory";
import useRack from "../../../services/Inventory/useRack";
import { GlobalContext } from "../../../Context/GlobalContext";
import {
  List,
  AlertCircle,
  Package,
  Info,
  PackageCheck,
  Layers,
  Plus,
  Eye,
  RotateCcw,
} from "lucide-react";

import { toast } from "react-toastify";
import AddItemVariantModal from "./AddItemVariantModal";
import AddVariantItemModal from "./AddVariantItemsModal";
import VariantStatusModal from "./VariantStatusModel";
import ViewItemModal from "./ViewItemModal";
import LoadingScreen from "../../../utils/LoadingScreen";
import useMedia from "../../../services/Whatsapp/useMedia";

const Catalogue = () => {
  const navigate = useNavigate();
  const { userID } = useContext(GlobalContext);

  const {
    catalogue,
    rawInventory,
    rawInventoryLoading,
    fetchCatalogue,
    updateItemStock,
    changeAvailability,
    updateVariantVisibility,
    updateCategoryVisibility,
    updateCustomization,
    assignPrinterToItem,
    resetStock,
    fetchRawInventory,
    addRawInventoryMaster,
    getRecipeIngredients,
  } = useCatalogue();

  const { addPrinter, getPrinters, printerList } = useOrderSetting();
  const { uploadImage } = useMedia();

  const {
    enableDisablePackagingCharges,
    updateCatalogItem,
    getSubCategories,
    getFoodCustomizations,
    insertFoodCustomizations,
  } = useInventory();
  const { fetchAllInventoryVariants } = useGlobalInventory();
  const { fetchRacks, rows: rackRows } = useRack();
  const { getPackaging } = useOrderSetting();
  const [searchText, setSearchText] = useState("");
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCustomizations, setSelectedCustomizations] = useState([]);
  const [activeTab, setActiveTab] = useState("catalogue");
  const [rawItemName, setRawItemName] = useState("");
  const [addingRawItem, setAddingRawItem] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [optimisticStocks, setOptimisticStocks] = useState({});
  const [optimisticVisible, setOptimisticVisible] = useState({});
  const [showVariantStatusModal, setShowVariantStatusModal] = useState(false);
  const [variantList, setVariantList] = useState([]);
  const [viewModalItem, setViewModalItem] = useState(null);
  const [viewModalTab, setViewModalTab] = useState("details");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [subCategories, setSubCategories] = useState([]);
  const [editableDetails, setEditableDetails] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [catalogImage, setCatalogImage] = useState(null);
  const [catalogImageBase64, setCatalogImageBase64] = useState("");
  const [savingCatalog, setSavingCatalog] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const [ingredientsModalOpen, setIngredientsModalOpen] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [showAddVariantModal, setShowAddVariantModal] = useState(false);
  const [showTableVariantModal, setShowTableVariantModal] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [packagingConfig, setPackagingConfig] = useState(null);
  const [categoryVisibleState, setCategoryVisibleState] = useState({});
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [resettingStock, setResettingStock] = useState(false);
  const [foodTypeFilter, setFoodTypeFilter] = useState("all");

  const [customizationSets, setCustomizationSets] = useState([]);

  const loadFoodCustomizations = async () => {
    try {
      const data = await getFoodCustomizations();
      setCustomizationSets(Array.isArray(data) ? data : []);
      return data;
    } catch (error) {
      console.error("Load Food Customizations Error:", error);
      setCustomizationSets([]);
      return [];
    }
  };

  useEffect(() => {
    const init = async () => {
      setPageLoading(true);

      // await fetchCatalogue();
      // await fetchAllInventoryVariants();
      // await getPrinters();
      // await fetchRawInventory();

      await Promise.all([
        fetchCatalogue(),
        fetchAllInventoryVariants(),
        getPrinters(),
        fetchRawInventory(),
        loadFoodCustomizations(),
      ]);

      setPageLoading(false);
    };

    init();
  }, []);

  useEffect(() => {
    const loadPackaging = async () => {
      const res = await getPackaging();

      if (res?.response?.length > 0) {
        setPackagingConfig(res.response[0]);
      }
    };

    loadPackaging();
  }, []);

  const handleRefresh = async () => {
    try {
      setPageLoading(true);

      await fetchCatalogue();
      await fetchAllInventoryVariants();
      await getPrinters();
      await fetchRawInventory();
      await loadFoodCustomizations();

      // toast.success("Data refreshed");
    } catch (err) {
      console.error(err);
      toast.error("Failed to refresh");
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (!viewModalItem) return;

    const arr = [];

    if (viewModalItem.homeDeliveryAvailability) {
      arr.push("home_delivery");
    }

    if (viewModalItem.takeawayAvailability) {
      arr.push("takeaway");
    }

    if (viewModalItem.diningAvailability) {
      arr.push("dining");
    }

    setAvailability(arr);
  }, [viewModalItem]);

  const handleDetailChange = (e) => {
    const { name, value } = e.target;
    setEditableDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleCatalogImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image");
      return;
    }

    setCatalogImage(file);

    const reader = new FileReader();

    reader.onloadend = () => {
      const result = reader.result;

      setPreviewImage(result);

      const base64Only = String(result).split(",")[1];
      setCatalogImageBase64(base64Only);
    };

    reader.readAsDataURL(file);
  };

  const handleCategoryVisibilityToggle = async (category) => {
    try {
      const catId = String(category.cat_id || "");

      if (!catId) {
        toast.error("Category ID missing");
        return;
      }

      const currentVisible = getEffectiveCategoryVisibleValue(category) === "Y";
      const nextVisible = currentVisible ? "N" : "Y";

      const payload = {
        catid: catId,
        usCode: String(userID),
        isVisible: nextVisible,
      };

      setCategoryVisibleState((prev) => ({
        ...prev,
        [catId]: nextVisible,
      }));

      const success = await updateCategoryVisibility(payload);

      if (success) {
        // toast.success(
        //   nextVisible === "Y"
        //     ? "Category visibility ON"
        //     : "Category visibility OFF",
        // );

        await fetchCatalogue();
      } else {
        setCategoryVisibleState((prev) => ({
          ...prev,
          [catId]: currentVisible ? "Y" : "N",
        }));
      }
    } catch (err) {
      console.error("Category visibility toggle error:", err);
      toast.error("Failed to update category visibility");
    }
  };

  const handleSaveCatalog = async () => {
    try {
      setSavingCatalog(true);

      const formatExpiryDate = (dateStr) => {
        if (!dateStr) return "";

        const d = new Date(dateStr);

        if (isNaN(d.getTime())) return dateStr;

        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");

        return `${yyyy}/${mm}/${dd}`;
      };

      const selectedIndex = editableDetails.selectedPriceIndex ?? 0;

      const selectedPrice =
        viewModalItem?.prices?.[selectedIndex] ||
        viewModalItem?.prices?.[0] ||
        {};

      let finalImage = viewModalItem?.photo || "";

      // 1. Upload selected image first
      if (catalogImage && catalogImageBase64) {
        const imageName =
          catalogImage.name?.split(".")?.[0] ||
          editableDetails.item_name ||
          viewModalItem.item_name ||
          "item_image";

        const uploadRes = await uploadImage(imageName, catalogImageBase64);

        if (!uploadRes) {
          toast.error("Image upload failed");
          return;
        }

        finalImage =
          uploadRes?.response ||
          uploadRes?.image_url ||
          uploadRes?.imageUrl ||
          uploadRes?.url ||
          uploadRes?.path ||
          uploadRes?.data?.url ||
          uploadRes?.data?.image_url ||
          finalImage;

        if (!finalImage) {
          toast.error("Image uploaded but image URL not found");
          return;
        }
      }

      // 2. Update selected variant/price row
      const payload = {
        userid: String(userID),

        itemId: String(viewModalItem.item_id),

        id: String(
          editableDetails.id || selectedPrice.id || selectedPrice.row_id || "",
        ),

        itemName: String(editableDetails.item_name || ""),
        itemNameTrans: String(editableDetails.item_name_trans ?? ""),
        desc: String(editableDetails.item_description || ""),

        category: String(
          editableDetails.category ||
            editableDetails.sub_category ||
            viewModalItem.category_name ||
            "",
        ),

        categoryId: String(
          editableDetails.categoryId || viewModalItem.cat_id || "",
        ),

        size: String(editableDetails.size || selectedPrice.size || ""),

        sizeId: String(
          editableDetails.sizeId ||
            selectedPrice.size_id ||
            selectedPrice.sizeId ||
            "",
        ),

        variantName: String(
          editableDetails.variantName ||
            selectedPrice.variantName ||
            editableDetails.size ||
            selectedPrice.size ||
            "",
        ),

        variantId: String(
          editableDetails.variantId ||
            selectedPrice.variantId ||
            selectedPrice.size_id ||
            selectedPrice.sizeId ||
            "",
        ),

        taxPercentage: String(editableDetails.taxPercentage ?? ""),

        sellingPrice: String(editableDetails.sellingPrice ?? ""),

        homeDeliveryPrice: String(editableDetails.homeDeliveryPrice ?? ""),

        takewayPrice: String(
          editableDetails.takewayPrice || editableDetails.takeawayPrice || "",
        ),

        diningPrice: String(editableDetails.diningPrice ?? ""),

        roomOrder: String(
          editableDetails.roomOrderPrice || editableDetails.roomOrder || "",
        ),

        carOrder: String(
          editableDetails.carOrderPrice || editableDetails.carOrder || "",
        ),

        rackNo: String(
          editableDetails.rackNo || editableDetails.rackNumber || "",
        ),

        expiryDate: formatExpiryDate(editableDetails.expiryDate),

        image: finalImage,
      };

      const res = await updateCatalogItem(payload);

      console.log("Update Catalogue Response:", res);

      if (res?.responseCode === "00") {
        toast.success(res.responseMessage || "Item updated successfully!");

        setEditModalOpen(false);
        setCatalogImage(null);
        setCatalogImageBase64("");
        setPreviewImage(null);

        await fetchCatalogue();
      } else {
        toast.error(res?.responseMessage || "Failed to update item");
      }
    } catch (err) {
      console.error("Save Catalogue Error:", err);
      toast.error("Something went wrong");
    } finally {
      setSavingCatalog(false);
    }
  };

  const handleAvailabilityChange = (type) => {
    setAvailability((prev) => {
      if (prev.includes(type)) {
        return prev.filter((item) => item !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const handleSaveAvailability = async () => {
    try {
      const payload = {
        userid: userID,
        itemId: viewModalItem.item_id,

        homeDelivery: availability.includes("home_delivery"),
        takeaway: availability.includes("takeaway"),
        dining: availability.includes("dining"),
      };

      const success = await changeAvailability(payload);

      if (success) {
        toast.success("Availability updated!");
        fetchCatalogue();
      }
    } catch (err) {
      toast.error("Failed to update availability");
    }
  };

  const handleSavePrinter = async () => {
    try {
      const payload = {
        userid: userID,
        itemId: viewModalItem.item_id,
        printerId: selectedPrinter,
      };

      const success = await assignPrinterToItem(payload);

      if (success) {
        toast.success("Printer assigned!");
      }
    } catch (err) {
      toast.error("Failed to assign printer");
    }
  };

  const handleAddPrinter = async () => {
    try {
      await addPrinter({ userid: userID });
      await getPrinters();
      toast.success("Printer added!");
    } catch (err) {
      toast.error("Failed to add printer");
    }
  };

  const handleFetchRacks = async () => {
    await fetchRacks();
  };

  const handleGetSubCategories = async (query) => {
    try {
      const data = await getSubCategories(query);
      return data || [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const parseDishValue = (dish) => {
    try {
      const parsed = JSON.parse(dish || "[]");
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  };

  const getFoodType = (item) => {
    const iconUrl = String(item?.icon_dish?.[0] || "").toLowerCase();
    const dishValues = parseDishValue(item?.dish);

    // API safe checks
    if (iconUrl.includes("non") || iconUrl.includes("nonveg")) {
      return "nonveg";
    }

    if (iconUrl.includes("veg")) {
      return "veg";
    }

    // Common mapping fallback:
    // dish ["1"] = veg, dish ["2"] = non-veg
    if (dishValues.includes("2")) {
      return "nonveg";
    }

    if (dishValues.includes("1")) {
      return "veg";
    }

    // Fallback from isVeg if backend sends proper value
    if (String(item?.isVeg || "").toUpperCase() === "Y") {
      return "veg";
    }

    if (String(item?.isVeg || "").toUpperCase() === "N") {
      return "nonveg";
    }

    return "veg";
  };

  const FoodTypeBadge = ({ item }) => {
    const type = getFoodType(item);
    const iconUrl = item?.icon_dish?.[0];

    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
          type === "veg"
            ? "border-green-200 bg-green-50 text-green-700"
            : "border-red-200 bg-red-50 text-red-700"
        }`}
        title={type === "veg" ? "Veg" : "Non Veg"}
      >
        {iconUrl ? (
          <img
            src={iconUrl}
            alt={type === "veg" ? "Veg" : "Non Veg"}
            className="h-3.5 w-3.5 object-contain"
          />
        ) : (
          <span
            className={`h-2.5 w-2.5 rounded-full ${
              type === "veg" ? "bg-green-600" : "bg-red-600"
            }`}
          />
        )}

        {type === "veg" ? "Veg" : "Non Veg"}
      </span>
    );
  };

  const filteredCategories = useMemo(() => {
    if (!catalogue) return [];

    return catalogue
      .map((cat) => {
        const items = (cat.menu_items || []).filter((item) => {
          const matchSearch = item.item_name
            ?.toLowerCase()
            .includes(searchText.toLowerCase());

          if (!matchSearch) return false;

          const itemFoodType = getFoodType(item);

          if (foodTypeFilter !== "all" && itemFoodType !== foodTypeFilter) {
            return false;
          }

          const prices = item.prices || [];

          // OUT OF STOCK TAB
          if (activeTab === "out-of-stock") {
            return prices.some((p) => {
              const variantId = String(p.id || p.row_id || p.size_id || "");

              const effectiveStock =
                optimisticStocks[variantId] !== undefined
                  ? optimisticStocks[variantId]
                  : p.inStock;

              return effectiveStock === "N";
            });
          }

          // INVISIBLE TAB
          if (activeTab === "invisible") {
            return prices.some((p) => {
              const variantId = String(p.id || p.row_id || p.size_id || "");

              const effectiveVisible =
                optimisticVisible[variantId] !== undefined
                  ? optimisticVisible[variantId]
                  : p.visibility;

              return effectiveVisible === "N";
            });
          }

          return true;
        });

        return { ...cat, menu_items: items };
      })
      .filter((cat) => cat.menu_items.length > 0);
  }, [
    catalogue,
    searchText,
    activeTab,
    foodTypeFilter,
    optimisticStocks,
    optimisticVisible,
  ]);

  const filteredRawInventory = useMemo(() => {
    if (!rawInventory) return [];

    return rawInventory.filter((item) =>
      (item.item_name || item.name || "")
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    );
  }, [rawInventory, searchText]);

  const getSelectedCustomizationIds = (category) => {
    try {
      const appliedCustomizations = JSON.parse(category?.customization || "[]");

      if (!Array.isArray(appliedCustomizations)) {
        return [];
      }

      return customizationSets
        .filter((master) => {
          return appliedCustomizations.some((applied) => {
            const sameField =
              String(applied?.customize_field || "")
                .trim()
                .toLowerCase() ===
              String(master?.customize_field || "")
                .trim()
                .toLowerCase();

            const masterOptions = Array.isArray(master?.options)
              ? master.options.map(String)
              : [];

            const appliedOptions = Array.isArray(applied?.options)
              ? applied.options.map(String)
              : [];

            const hasMatchingOption = masterOptions.some((option) =>
              appliedOptions.includes(option),
            );

            return sameField && hasMatchingOption;
          });
        })
        .map((item) => String(item.pk_customization_id));
    } catch (error) {
      console.error("Customization parse error:", error);
      return [];
    }
  };

  const handleResetStock = async () => {
    try {
      setResettingStock(true);

      const success = await resetStock({ userid: userID });

      if (success) {
        // toast.success("Stock reset successfully");
        setShowResetConfirmModal(false);
        await fetchCatalogue();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to reset stock");
    } finally {
      setResettingStock(false);
    }
  };

  const handleAddRawItem = async () => {
    if (!rawItemName.trim()) {
      toast.error("Please enter item name");
      return;
    }

    try {
      setAddingRawItem(true);
      await addRawInventoryMaster(rawItemName);
      toast.success("Raw item added successfully");
      setRawItemName("");
      fetchRawInventory();
    } catch (error) {
      toast.error("Failed to add raw item");
    } finally {
      setAddingRawItem(false);
    }
  };

  const getCategoryVisibleValue = (category) => {
    const catId = String(category.cat_id || "");

    if (categoryVisibleState[catId] !== undefined) {
      return categoryVisibleState[catId];
    }

    return (
      category.isVisible ||
      category.visibility ||
      category.is_visible ||
      category.category_visibility ||
      "Y"
    );
  };

  const getEffectiveCategoryVisibleValue = (category) => {
    const catId = String(category.cat_id || "");

    // If category toggle was clicked manually, optimistic category state first
    if (categoryVisibleState[catId] !== undefined) {
      return categoryVisibleState[catId];
    }

    const allPrices = (category.menu_items || []).flatMap(
      (item) => item.prices || [],
    );

    // If no variants/prices exist, fallback to category API value
    if (allPrices.length === 0) {
      return getCategoryVisibleValue(category);
    }

    // If even one variant is visible, category button should show ON
    const hasAnyVisibleVariant = allPrices.some((price) => {
      const variantId = String(price.id || price.row_id || price.size_id || "");

      const effectiveVisible =
        optimisticVisible[variantId] !== undefined
          ? optimisticVisible[variantId]
          : price.visibility;

      return effectiveVisible === "Y";
    });

    return hasAnyVisibleVariant ? "Y" : "N";
  };

  const handlePackagingToggle = async (item) => {
    try {
      const current = item.packaging_charge === "Y" ? "Y" : "N";

      const next = current === "Y" ? "N" : "Y";

      const payload = {
        usCode: userID,
        itemId: item.item_id,
        trigger: next,
      };

      const success = await enableDisablePackagingCharges(payload);

      if (success) {
        toast.success(
          next === "Y" ? "Packaging Enabled" : "Packaging Disabled",
        );

        fetchCatalogue();
      }
    } catch (err) {
      toast.error("Failed to toggle packaging");
    }
  };

  const handleVariantStockToggle = async (item, price) => {
    const variantId = String(price.id || price.row_id || price.size_id || "");

    if (!variantId) {
      toast.error("Variant ID missing");
      return;
    }

    const currentStock = price.inStock === "Y";
    const nextStock = currentStock ? "N" : "Y";

    const currentVisible = price.visibility === "Y" ? "Y" : "N";

    setOptimisticStocks((prev) => ({
      ...prev,
      [variantId]: nextStock,
    }));

    try {
      const payload = {
        id: variantId,
        isVisible: currentVisible,
        isStock: nextStock,
      };

      const success = await updateVariantVisibility(payload);

      if (success) {
        // toast.success(nextStock === "Y" ? "Stock ON" : "Stock OFF");
        await fetchCatalogue();
      } else {
        setOptimisticStocks((prev) => ({
          ...prev,
          [variantId]: currentStock ? "Y" : "N",
        }));
      }
    } catch (err) {
      console.error(err);

      setOptimisticStocks((prev) => ({
        ...prev,
        [variantId]: currentStock ? "Y" : "N",
      }));

      toast.error("Failed to update stock");
    }
  };

  const handleVariantVisibleToggle = async (item, price) => {
    const variantId = String(price.id || price.row_id || price.size_id || "");

    if (!variantId) {
      toast.error("Variant ID missing");
      return;
    }

    const currentVisible = price.visibility === "Y";
    const nextVisible = currentVisible ? "N" : "Y";

    const currentStock = price.inStock === "Y" ? "Y" : "N";

    setOptimisticVisible((prev) => ({
      ...prev,
      [variantId]: nextVisible,
    }));

    try {
      const payload = {
        id: variantId,
        isVisible: nextVisible,
        isStock: currentStock,
      };

      const success = await updateVariantVisibility(payload);

      if (success) {
        // toast.success(nextVisible === "Y" ? "Visible ON" : "Visible OFF");
        await fetchCatalogue();
      } else {
        setOptimisticVisible((prev) => ({
          ...prev,
          [variantId]: currentVisible ? "Y" : "N",
        }));
      }
    } catch (err) {
      console.error(err);

      setOptimisticVisible((prev) => ({
        ...prev,
        [variantId]: currentVisible ? "Y" : "N",
      }));

      toast.error("Failed to update visibility");
    }
  };

  const handleInsertFoodCustomization = async ({ customizeField, options }) => {
    const result = await insertFoodCustomizations({
      customizeField,
      options,
    });

    if (!result) {
      return false;
    }

    await loadFoodCustomizations();
    return true;
  };

const handleApplyCustomization = async () => {
  const payload = {
    userid: userID,
    id: selectedCategory.cat_id,
    type: "category",
    operation: "add",

    customizations: customizationSets
      .filter((set) =>
        selectedCustomizations.includes(String(set.pk_customization_id)),
      )
      .map((set) => ({
        customization_type: "customize",
        customize_field: set.customize_field,
        options: set.options,
      })),
  };

  console.log("Customization Payload:", payload);

  const success = await updateCustomization(payload);

  if (success) {
    setShowCustomizationModal(false);
    fetchCatalogue();
  }
};

  useEffect(() => {
    if (!viewModalItem) return;

    setEditModalOpen(false);
    setPreviewImage(null);
    setCatalogImage(null);
    setCatalogImageBase64("");
  }, [viewModalItem]);

  useEffect(() => {
    if (!viewModalItem || !Array.isArray(printerList)) return;

    const found = printerList.find(
      (p) =>
        p.printerAlias === viewModalItem.printer_alias ||
        p.printerName === viewModalItem.printer_code,
    );

    setSelectedPrinter(found?.id || "");
  }, [viewModalItem, printerList]);

  useEffect(() => {
    if (editModalOpen && printerList?.length > 0) {
      setSelectedPrinter(printerList[0].id);
    }
  }, [editModalOpen, printerList]);

  console.log("GLOBAL PRINTER LIST:", printerList);
  const getCategoryKey = (category) =>
    `${category.cat_id}_${category.category_name}`;

  const toggleCategory = (key) => {
    setExpandedCategories((prev) =>
      prev.includes(key) ? prev.filter((i) => i !== key) : [...prev, key],
    );
  };

  const getVariantLabel = (price) => {
    const candidates = [
      price?.variantName,
      price?.variant_name,
      price?.variation,
      price?.variation_name,
      price?.variant_title,
      price?.size,
      price?.sizeName,
      price?.size_name,
      price?.variantId,
      price?.variant_id,
      price?.sizeId,
      price?.size_id,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    // Do not let a generic size="Default" hide variantName="Half/Full".
    const meaningfulLabel = candidates.find(
      (value) => value.toLowerCase() !== "default",
    );

    return meaningfulLabel || candidates[0] || "Default";
  };

  return (
    <>
      <div className="p-5 min-h-0 h-full flex flex-col overflow-hidden montserrat-font-1">
        <div className="sticky top-0 z-30">
          <div className="flex justify-between items-center mb-4 montserrat-font-1">
            <h1 className="text-2xl font-semibold">Catalogue Items</h1>
            {/* Buttons and top search bar */}
            <div className="flex gap-3 montserrat-font-1">
              <input
                type="text"
                placeholder="Search items..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="px-3 py-1 border rounded-lg bg-white shadow-sm focus:outline-none"
              />

              {activeTab !== "raw-inventory" && (
                <div className="flex h-8 items-center rounded-lg border bg-white shadow-sm">
                  {[
                    { key: "all", label: "All" },
                    { key: "veg", label: "Veg" },
                    { key: "nonveg", label: "Non Veg" },
                  ].map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setFoodTypeFilter(option.key)}
                      className={`h-8 rounded-md px-3 text-sm py-1.5 text-center font-semibold transition ${
                        foodTypeFilter === option.key
                          ? option.key === "veg"
                            ? "bg-green-100 text-green-700"
                            : option.key === "nonveg"
                              ? "bg-red-100 text-red-700"
                              : "bg-blue-500 text-white"
                          : "text-gray-500 hover:bg-gray-100"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}

              {activeTab !== "raw-inventory" && (
                <div className="flex gap-3">
                  <button
                    onClick={() => navigate("/Orders/Catalogue/add-items")}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 h-8 text-sm rounded-md flex items-center"
                  >
                    Add Items
                  </button>

                  <button
                    onClick={() => {
                      setSelectedItem(null);
                      setShowAddVariantModal(true);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 h-8 text-sm rounded-md flex items-center"
                  >
                    Add Varients
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowResetConfirmModal(true)}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 h-8 text-sm rounded-md flex items-center"
                  >
                    Reset Stock
                  </button>

                  {/*  Refresh Button */}
                  <button
                    onClick={handleRefresh}
                    className="flex items-center gap-2 px-3 h-8 text-sm rounded-md border text-white bg-green-500"
                  >
                    <span className={pageLoading ? "animate-spin" : ""}>
                      <RotateCcw className="w-4 h-4" />
                    </span>
                    Refresh
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* TABS */}
          <div className="border-b mb-6 montserrat-font-1">
            <div className="flex gap-6 text-sm font-medium">
              <button
                onClick={() => setActiveTab("catalogue")}
                className={`pb-2 flex items-center gap-2 border-b-2 transition ${
                  activeTab === "catalogue"
                    ? "border-green-600 text-green-600"
                    : "border-transparent text-gray-500 hover:text-green-600"
                }`}
              >
                <List size={16} />
                Catalogue
              </button>

              <button
                onClick={() => setActiveTab("out-of-stock")}
                className={`pb-2 flex items-center gap-2 border-b-2 transition ${
                  activeTab === "out-of-stock"
                    ? "border-green-600 text-green-600"
                    : "border-transparent text-gray-500 hover:text-green-600"
                }`}
              >
                <AlertCircle size={16} />
                Out of Stock
              </button>

              <button
                onClick={() => setActiveTab("invisible")}
                className={`pb-2 flex items-center gap-2 border-b-2 transition ${
                  activeTab === "invisible"
                    ? "border-green-600 text-green-600"
                    : "border-transparent text-gray-500 hover:text-green-600"
                }`}
              >
                <Eye size={16} />
                Invisible Stock
              </button>

              <button
                onClick={() => setActiveTab("raw-inventory")}
                className={`pb-2 flex items-center gap-2 border-b-2 transition ${
                  activeTab === "raw-inventory"
                    ? "border-green-600 text-green-600"
                    : "border-transparent text-gray-500 hover:text-green-600"
                }`}
              >
                <Package size={16} />
                Raw Inventory
              </button>

              <div className="relative group">
                <button className="pb-2 flex items-center gap-2 text-gray-500 hover:text-green-600">
                  <Info size={16} />
                </button>

                <div
                  className="absolute right-0 top-8 w-[380px] bg-white border rounded-xl shadow-lg z-50 
                  opacity-0 invisible group-hover:opacity-100 group-hover:visible 
                  transition-all duration-200"
                >
                  <div className="px-4 py-3 border-b font-semibold">
                    Icons Guide
                  </div>

                  <div className="p-4 space-y-3 text-sm montserrat-font-1">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-green-600 text-white">
                        <Eye size={14} />
                      </div>
                      <p>
                        <b>View</b> — open item details & edit
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-500 text-white">
                        <Plus size={14} />
                      </div>
                      <p>
                        <b>Add Variant</b> — create new variant
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-blue-600 text-white">
                        <Layers size={14} />
                      </div>
                      <p>
                        <b>Manage Variants</b> — update visibility/stock
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-red-500 text-white">
                        <Package size={14} />
                      </div>
                      <p>
                        <b>Packaging OFF</b>
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-green-600 text-white">
                        <PackageCheck size={14} />
                      </div>
                      <p>
                        <b>Packaging ON</b>
                      </p>
                    </div>

                    <div className="text-xs text-gray-500 pt-2">
                      <p>
                        <b>Checkboxes:</b>
                      </p>
                      <p>• Visible — show/hide item</p>
                      <p>• Stock — in stock / out of stock</p>
                    </div>
                    <p className="mt-2">
                      <b>Price Labels:</b>
                    </p>
                    <p>• HD — Home Delivery</p>
                    <p>• TA — Takeaway</p>
                    <p>• DI — Dining</p>
                    <p>• RO — Room Order</p>
                    <p>• CO — Car Order</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {/* CATEGORY LIST */}
          <CatalogueCategory
            activeTab={activeTab}
            pageLoading={pageLoading}
            filteredCategories={filteredCategories}
            getCategoryKey={getCategoryKey}
            expandedCategories={expandedCategories}
            toggleCategory={toggleCategory}
            setSelectedCategory={setSelectedCategory}
            setShowCustomizationModal={setShowCustomizationModal}
            setSelectedCustomizations={setSelectedCustomizations}
            getEffectiveCategoryVisibleValue={getEffectiveCategoryVisibleValue}
            handleCategoryVisibilityToggle={handleCategoryVisibilityToggle}
            optimisticStocks={optimisticStocks}
            optimisticVisible={optimisticVisible}
            getVariantLabel={getVariantLabel}
            handleVariantStockToggle={handleVariantStockToggle}
            handleVariantVisibleToggle={handleVariantVisibleToggle}
            catalogue={catalogue}
            setViewModalItem={setViewModalItem}
            setViewModalTab={setViewModalTab}
            packagingConfig={packagingConfig}
            handlePackagingToggle={handlePackagingToggle}
            FoodTypeBadge={FoodTypeBadge}
            setSelectedItem={setSelectedItem}
            setShowTableVariantModal={setShowTableVariantModal}
            rawItemName={rawItemName}
            setRawItemName={setRawItemName}
            handleAddRawItem={handleAddRawItem}
            addingRawItem={addingRawItem}
            rawInventoryLoading={rawInventoryLoading}
            filteredRawInventory={filteredRawInventory}
            getSelectedCustomizationIds={getSelectedCustomizationIds}
          />
        </div>

        <AddItemVariantModal
          show={showAddVariantModal}
          onHide={() => setShowAddVariantModal(false)}
          itemId={selectedItem?.item_id}
          itemName={selectedItem?.item_name}
          onSuccess={fetchCatalogue}
        />

        <AddVariantItemModal
          show={showTableVariantModal}
          onHide={() => setShowTableVariantModal(false)}
          itemId={selectedItem?.item_id}
          itemName={selectedItem?.item_name}
          onSuccess={fetchCatalogue}
        />

        <VariantStatusModal
          show={showVariantStatusModal}
          onClose={() => setShowVariantStatusModal(false)}
          variants={variantList}
          itemName={selectedItem?.item_name}
          onSave={async (variant) => {
            const payload = {
              id: String(variant.id || variant.row_id),
              isVisible: variant.visibility || variant.isVisible,
              isStock: variant.inStock === "Y" ? "Y" : "N",
            };
            const success = await updateVariantVisibility(payload);
            if (success) {
              toast.success("Updated Successfully");
              // setShowVariantStatusModal(false);
              await fetchCatalogue();
            }
          }}
        />

        <ViewItemModal
          viewModalItem={viewModalItem}
          setViewModalItem={setViewModalItem}
          viewModalTab={viewModalTab}
          setViewModalTab={setViewModalTab}
          editModalOpen={editModalOpen}
          setEditModalOpen={setEditModalOpen}
          fetchRacks={handleFetchRacks}
          getSubCategories={handleGetSubCategories}
          setSubCategories={setSubCategories}
          subCategories={subCategories}
          rackList={rackRows || []}
          editableDetails={editableDetails}
          setEditableDetails={setEditableDetails}
          handleDetailChange={handleDetailChange}
          previewImage={previewImage}
          handleCatalogImageChange={handleCatalogImageChange}
          handleSaveCatalog={handleSaveCatalog}
          availability={availability}
          handleAvailabilityChange={handleAvailabilityChange}
          handleSaveAvailability={handleSaveAvailability}
          selectedPrinter={selectedPrinter}
          setSelectedPrinter={setSelectedPrinter}
          printerList={printerList || []}
          handleSavePrinter={handleSavePrinter}
          handleAddPrinter={handleAddPrinter}
          getRecipeIngredients={getRecipeIngredients}
          setIngredients={setIngredients}
          setIngredientsModalOpen={setIngredientsModalOpen}
          getPrinters={getPrinters}
          savingCatalog={savingCatalog}
          handleVariantStockToggle={handleVariantStockToggle}
          handleVariantVisibleToggle={handleVariantVisibleToggle}
          optimisticStocks={optimisticStocks}
          optimisticVisible={optimisticVisible}
        />
      </div>

      {/* Catalogue model */}
      <CatalogueModals
        showResetConfirmModal={showResetConfirmModal}
        setShowResetConfirmModal={setShowResetConfirmModal}
        resettingStock={resettingStock}
        handleResetStock={handleResetStock}
        showCustomizationModal={showCustomizationModal}
        setShowCustomizationModal={setShowCustomizationModal}
        customizationSets={customizationSets}
        selectedCustomizations={selectedCustomizations}
        setSelectedCustomizations={setSelectedCustomizations}
        handleApplyCustomization={handleApplyCustomization}
        handleInsertFoodCustomization={handleInsertFoodCustomization}
      />
    </>
  );
};

export default Catalogue;
